'use client';

import { useEffect, useId, useRef, useState } from 'react';
import { useTranslations } from 'next-intl';
import { cardVariants } from '@/components/ui/card';
import { cn } from '@/lib/utils';

/**
 * The organiser's description, collapsed to a few lines when it is long — on
 * phones and tablets only; from `lg` up it always shows in full. The whole
 * text is always in the DOM (crawlers, find-in-page); collapsing is a
 * max-height. The toggle appears only when the text overflows the collapsed
 * height, so a short description shows in full with no button.
 */
export function EventAbout({ description }: { description: string }) {
  const t = useTranslations('Event');
  const body = useRef<HTMLDivElement>(null);
  const bodyId = useId();
  const [open, setOpen] = useState(false);
  const [overflows, setOverflows] = useState(false);

  useEffect(() => {
    const el = body.current;
    if (!el) return;
    const measure = () => {
      // Measure against the collapsed box only; an open body always "fits".
      if (!open) setOverflows(el.scrollHeight > el.clientHeight + 1);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [description, open]);

  return (
    <section className={cn(cardVariants.base, 'p-4 md:p-6')}>
      <h2 className="text-foreground text-md mb-2 font-medium">{t('description.title')}</h2>
      <div className="relative">
        <div
          id={bodyId}
          ref={body}
          className={cn(
            'prose prose-sm max-w-none overflow-hidden',
            !open && 'max-h-56 lg:max-h-none',
          )}
          dangerouslySetInnerHTML={{ __html: description }}
        />
        {overflows && !open && (
          <div
            aria-hidden
            className="from-card pointer-events-none absolute inset-x-0 bottom-0 h-12 bg-gradient-to-t to-transparent lg:hidden"
          />
        )}
      </div>
      {overflows && (
        <button
          type="button"
          aria-expanded={open}
          aria-controls={bodyId}
          onClick={() => setOpen((v) => !v)}
          className="text-accent mt-1 min-h-11 cursor-pointer text-sm font-semibold lg:hidden"
        >
          {open ? t('description.showLess') : t('description.showMore')}
        </button>
      )}
    </section>
  );
}
